import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { getCapture, createStudentFromCapture, dismissCapture } from '../../lib/api'
import { keys } from '../../lib/queryKeys'
import { PageHeader } from '../../components/PageHeader'
import { StudentForm, type StudentFormValues } from '../../components/StudentForm'
import { m } from '../../paraglide/messages'
import type { PendingCapture } from '../../types'

export const Route = createFileRoute('/capture/$id')({
  component: CapturePage,
})

const fmtDT = new Intl.DateTimeFormat('he-IL', {
  weekday: 'long', day: '2-digit', month: '2-digit',
  hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem',
})

function CapturePage() {
  const { id }      = Route.useParams()
  const navigate    = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState('')

  const { data: capture, isLoading, error: loadErr } = useQuery<PendingCapture>({
    queryKey: keys.capture(id),
    queryFn:  () => getCapture(id),
    retry:    false,
  })

  const create = useMutation({
    mutationFn: (v: StudentFormValues) => createStudentFromCapture(id, {
      name:  v.name,
      phone: v.phone || undefined,
      email: v.email || undefined,
      rate:  v.rate,
      notes: v.notes || undefined,
    }),
    onSuccess: ({ lessonId }) => {
      queryClient.invalidateQueries({ queryKey: keys.students })
      queryClient.invalidateQueries({ queryKey: keys.lessons })
      queryClient.invalidateQueries({ queryKey: keys.captures })
      navigate({ to: '/lessons/$id', params: { id: String(lessonId) } })
    },
    onError: (err) => setError((err as Error).message),
  })

  const dismiss = useMutation({
    mutationFn: () => dismissCapture(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.captures })
      navigate({ to: '/lessons' })
    },
  })

  if (isLoading) return <p className="text-zinc-400">{m.loading()}</p>
  if (loadErr || !capture) {
    return (
      <div>
        <PageHeader title={m.capture_create_student()} />
        <p className="text-sm text-zinc-500">
          {m.capture_handled()}{' '}
          <Link to="/lessons" className="underline hover:text-zinc-900">{m.lesson_back()}</Link>
        </p>
      </div>
    )
  }

  const when   = fmtDT.format(new Date(capture.scheduledAt))
  const isPast = new Date(capture.scheduledAt).getTime() < Date.now()

  return (
    <div>
      <PageHeader title={m.capture_create_student_q()} />

      <div className="border border-zinc-200 p-4 mb-6 max-w-md">
        <p className="text-sm text-zinc-600">
          {m.capture_no_match()}
        </p>
        <p className="text-base font-semibold text-zinc-900 mt-1">{capture.studentName}</p>
        <p className="text-sm text-zinc-500 mt-1">{when} · {capture.durationMin} min</p>
        <p className="text-xs text-zinc-400 mt-2">
          {isPast ? m.capture_past() : m.capture_future()}
        </p>
      </div>

      <StudentForm
        initial={{ name: capture.studentName }}
        submitLabel={m.capture_submit()}
        pendingLabel={m.capture_creating()}
        pending={create.isPending}
        error={error}
        onSubmit={(v) => { setError(''); create.mutate(v) }}
        footer={
          <button
            type="button"
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isPending}
            className="border border-zinc-300 text-zinc-500 px-3 py-1.5 text-sm hover:border-red-600 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {dismiss.isPending ? m.capture_dismissing() : m.capture_dismiss()}
          </button>
        }
      />
    </div>
  )
}
